import playerLoader, { type BrightcovePlayer } from "@brightcove/player-loader"
import { type ReactElement, useEffect, useRef } from "react"
import styled from "styled-components"
import { playerLog, playerWarn } from "../lib/playerLog"

/**
 * Video.js configuration. `controls: false` is the whole point of this screen —
 * everything the viewer sees is `PlayerControls`, so the built-in control bar and
 * big play button must never render, not even for a frame while the player boots.
 */
const PLAYER_OPTIONS = {
  controls: false,
  playsinline: true,
  preload: "auto",
  fill: true,
} as const

/**
 * Only exists to keep `player-loader` from throwing inside its own error
 * handler. `initPlayer` catches a `bc()` failure and then reads
 * `params.embedOptions.tagName` without a guard, so when `embedOptions` is
 * absent the catch block throws, `reject()` never runs, and the promise hangs
 * forever — the real error is lost and the player sits on a spinner. Every other
 * read of `embedOptions` in that file is guarded; this one is not.
 *
 * `video-js` is the tag it already defaults to, so this changes nothing about
 * the embed itself. See FINDINGS.md.
 */
const EMBED_OPTIONS = { tagName: "video-js" } as const

/**
 * The loader inserts a `video-js` element into this node, so the sizing rules
 * have to reach through markup this component does not write.
 * `pointer-events: none` sends every click to the controls overlay above — the
 * viewer should never hit a native context menu or a stray Video.js handler.
 */
const Frame = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.player.surface};

  .video-js,
  video {
    width: 100%;
    height: 100%;
  }

  .video-js {
    pointer-events: none;
  }

  video {
    object-fit: contain;
  }

  /*
   * Hidden here rather than switched off through Video.js options.
   * Passing errorDisplay, loadingSpinner or bigPlayButton as false deletes those
   * child components outright, and the Brightcove skin's plugins expect them to
   * exist — videojs-errors in particular. Removing them made the player throw
   * during init. CSS gets the same look without taking the components away from
   * the plugins that reach for them.
   */
  .vjs-control-bar,
  .vjs-big-play-button,
  .vjs-loading-spinner,
  .vjs-error-display,
  .vjs-errors-dialog {
    display: none !important;
  }

  /* Captions sit clear of the scrub bar, matching where Video.js puts them when
     a control bar is present. */
  .vjs-text-track-display > div {
    bottom: 6rem !important;
  }
`

interface BrightcoveVideoProps {
  readonly accountId: string
  readonly videoCloudId: string
  /**
   * Video Cloud Analytics viewer id — the OIDC `sub`, matching what the native
   * players send. Absent when the viewer is not signed in.
   */
  readonly viewerId: string | undefined
  /** Remount key. Changing it disposes and rebuilds the embed — used to retry. */
  readonly reloadKey: number
  readonly onReady: (player: BrightcovePlayer) => void
  readonly onFailure: () => void
}

/**
 * The Brightcove embed, and nothing else.
 *
 * We call `@brightcove/player-loader` directly rather than using
 * `@brightcove/react-player-loader`. Be aware that this is a preference, not a
 * technical necessity: this file was originally written to escape a StrictMode
 * hang blamed on the wrapper, and that diagnosis was wrong — the hang was the
 * missing `embedOptions` above. `apps/ministry-grid` runs the same wrapper at
 * the same version quite happily, and it would handle a session change through
 * the catalog too (`videoId` is one of its `UPDATEABLE_PROPS`).
 *
 * What owning the effect actually buys is small but real: ~40 lines we can
 * instrument directly, and no class component in a hooks-only codebase. See
 * FINDINGS.md gaps 13 and 17 before revisiting this.
 */
export const BrightcoveVideo = ({
  accountId,
  videoCloudId,
  viewerId,
  reloadKey,
  onReady,
  onFailure,
}: BrightcoveVideoProps): ReactElement => {
  const frameRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<BrightcovePlayer | null>(null)

  /**
   * Read through refs so a changed callback or viewer id never re-runs the
   * effect below — rebuilding the player is expensive and visible.
   */
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  const onFailureRef = useRef(onFailure)
  onFailureRef.current = onFailure
  const viewerIdRef = useRef(viewerId)
  viewerIdRef.current = viewerId

  // Deliberately excludes `videoCloudId`: a session change swaps the source in
  // the effect below rather than rebuilding the player.
  // biome-ignore lint/correctness/useExhaustiveDependencies: videoCloudId is handled by the catalog effect, by design
  useEffect(() => {
    const refNode = frameRef.current

    if (!refNode) {
      return
    }

    /**
     * StrictMode runs this effect twice. The second run must not adopt a player
     * the first run's cleanup is about to dispose, so the cleanup flips this and
     * the resolved callback checks it — the same guard the React wrapper gets
     * wrong.
     */
    let cancelled = false
    let settled = false

    const scriptUrl = playerLoader.getUrl({ accountId })

    playerLog("embed loading", { accountId, videoCloudId, reloadKey, scriptUrl, connected: refNode.isConnected })

    /**
     * TEMPORARY, with `playerLog`.
     *
     * `player-loader` settles only from the injected script's `onload` or
     * `onerror`. If neither fires, the promise hangs forever and there is
     * nothing to catch — so this reports what the DOM actually looks like at
     * that point, which is the only way to tell "script was never inserted"
     * from "script is in the document but never ran".
     */
    const watchdog = setTimeout(() => {
      if (settled) {
        return
      }

      const scripts = [...refNode.querySelectorAll("script")].map(script => ({
        src: script.getAttribute("src"),
        connected: script.isConnected,
      }))

      playerWarn("loader has not settled after 6s", {
        scriptUrl,
        refNodeConnected: refNode.isConnected,
        scriptsInFrame: scripts,
        brightcoveScriptsInDocument: document.querySelectorAll('script[src*="players.brightcove.net"]').length,
        bcGlobal: typeof (window as { bc?: unknown }).bc,
        mediaElements: refNode.querySelectorAll("video-js, video, .video-js").length,
        frameHtml: refNode.innerHTML.slice(0, 300),
      })
    }, 6_000)

    void playerLoader({
      accountId,
      videoId: videoCloudId,
      embedType: "in-page",
      options: PLAYER_OPTIONS,
      embedOptions: EMBED_OPTIONS,
      refNode,
      refNodeInsert: "append",
    })
      .then(({ ref, type }) => {
        settled = true

        if (cancelled) {
          playerLog("embed resolved after cleanup; disposing")
          ref.dispose()
          return
        }

        playerLog("loader resolved", { type, hasRef: Boolean(ref) })
        playerRef.current = ref

        /**
         * Analytics has to be attached before the first play, and `analytics` is
         * only populated once the Brightcove skin has initialised — hence
         * `ready` rather than assigning straight onto the reference.
         */
        ref.ready(() => {
          const id = viewerIdRef.current

          if (!ref.analytics) {
            playerWarn("player exposes no analytics object; viewer id not sent")
            return
          }

          if (id) {
            ref.analytics.user = id
            playerLog("analytics viewer id set")
          } else {
            playerLog("no viewer id available; analytics user left unset")
          }
        })

        onReadyRef.current(ref)
      })
      .catch((error: unknown) => {
        settled = true

        if (cancelled) {
          return
        }

        playerWarn("loader rejected", error)
        onFailureRef.current()
      })

    return () => {
      cancelled = true
      clearTimeout(watchdog)

      const existing = playerRef.current
      playerRef.current = null

      if (existing && !existing.isDisposed()) {
        playerLog("disposing player")
        existing.dispose()
      }
    }
  }, [accountId, reloadKey])

  /**
   * A session change. The catalog swaps the source on the live player, which is
   * what keeps the rail from rebuilding the runtime on every selection. Skipped
   * on the first run because the initial `videoId` is already loaded above.
   */
  const loadedVideoIdRef = useRef<string | null>(null)
  useEffect(() => {
    const player = playerRef.current

    if (!player || player.isDisposed()) {
      loadedVideoIdRef.current = videoCloudId
      return
    }

    if (loadedVideoIdRef.current === videoCloudId) {
      return
    }

    loadedVideoIdRef.current = videoCloudId

    if (!player.usingPlugin("catalog")) {
      playerWarn("player has no catalog plugin; cannot swap source in place")
      return
    }

    playerLog("catalog swap", { videoCloudId })
    player.catalog?.get({ type: "video", id: videoCloudId }, (error, video) => {
      if (error) {
        playerWarn("catalog get failed", error)
        onFailureRef.current()
        return
      }

      if (!player.isDisposed()) {
        player.catalog?.load(video)
      }
    })
  }, [videoCloudId])

  return <Frame ref={frameRef} data-qa-hook="player-video" />
}
